/* eslint-disable complexity */
import {
    LOCAL_STORAGE_KEYS,
    NEDGLOBE_MAP_RESOLUTION,
    CONTENT_TYPE_ID,
    SELECTION_TYPES,
    ORGANIZATION_IMAGES,
    MAP_INTERACTION_TIMEOUTS,
    MAP_MARGINS,
    DEFAULT_ANIMATION_MS,
    INTERFACE_PANEL_TRIGGERS,
    DEFAULT_WEB_CLUSTER_MIN_ZOOM_RESOLUTION,
    MESSAGE_TYPE
} from '../../../../constants';
import { getScaleFromResolution } from './../../../../services/map';
import { getCoordinateArray, squareToPoints, getAddressResolutionIndex, checkTmaValue,windowMessageReceiver } from '../../../../services/utilities/utilities';
import { layerFeaturesCanBePaged } from '../../../../services/map/feature/selection';
import { isMobileDevice } from './../../../../services/utilities/devices';
import { ScaleLine as OlScaleLine, Rotate as OlRotate } from 'ol/control';
import i18n from '../../../../i18n';

let clusterSelectionLock = false;

export const actions = {
    async startNedGlobe({ dispatch, commit, getters, state }, { mapElementId, webThemeParameters, accessibleMode = false }) {
        let webThemeId = webThemeParameters.tma;

        if (!state.appConfig) {
            await dispatch('setApplicationConfiguration');
        }

        commit('SET_ACCESSIBLE_MODE', accessibleMode);
        commit('SET_WEBTHEME_PARAMETERS', webThemeParameters);

        dispatch('setLoader', { loading: true });
        dispatch('devLog', ['start']);

        dispatch('devLog', ['user session']);
        const sessionId = Math.floor(Math.random() * 100000000000).toString();
        commit('SET_USERINFO', { sessionId });

        // also gets baselayers
        await dispatch('getOrganizationData', { webThemeId });

        if (!getters.getOrganizationId) {
            throw new Error('Webtheme not found');
        }

        dispatch('devLog', ['getScales']);
        await dispatch('getScales', { webThemeId });

        // set language
        i18n.locale = getters.getUserData.language.toLowerCase();

        // add scaleline to map
        const controls = [
            new OlScaleLine({ text: true, bar: true, minWidth: 106, maxWidth: 200, target: document.getElementById('scaleLine') }),
            new OlRotate({ target: document.getElementById('rotationButton'), tipLabel: i18n.t('layers3D.labelResetRotation') }),];

        dispatch('devLog', ['init map']);
        // needs to be done before themes
        commit('REGISTER_MAP', { name: null, id: mapElementId, controls });

        await dispatch('getWebThemeOptionsById', { webThemeId });
        await dispatch('getWebThemeLayersById', { mapName: 'main', webThemeId });
        await dispatch('getWebStorageLayersById', { mapName: 'main', webThemeId });

        // Get outputformats for layers
        await dispatch('getLayerWfsOutputFormats');

        const webTheme = getters.getWebTheme?.data;

        // For non mobile device use printsettings, for mobile, no printing
        const showPrint = !isMobileDevice() ? webTheme?.showPrint : false;

        commit('CONFIGURE_PANELS', {
            search: webTheme?.showSearch,
            legend: webTheme?.showLegenda,
            filter: webTheme?.showFilter,
            table: webTheme?.showTableView,
            measure: getters.getWebThemeFeedbackType > 0 && !getters.getWebThemeHasFeedbackType,
            welcome: webTheme?.showWelcomeMessage,
            print: showPrint,
            legendClose: webTheme?.showLegenda || !webTheme?.openLegenda,
            startedFromPortal: getters.getWebThemeParameters.startedFromPortal,
            isWebFeedbackPointGeometryOnly: getters.getWebThemeHasFeedbackType && getters.getWebThemeFeedbackHasPointGeoOnly
        });

        if (!webTheme?.openAdvancedSearchByStart) {
            commit('TOGGLE_LEGEND_PANEL', webTheme?.openLegenda === true);
        } else {
            commit('TOGGLE_ADVANCED_SEARCH_PANEL', true);
        }

        commit('UPDATE_INFORMATION_PANEL', { height: null, width: null, maxWidth: null });

        dispatch('devLog', ['map']);
        await dispatch('initMainMap');

        dispatch('startWelcomePanel', webTheme);
        dispatch('devLog', ['ready']);
        dispatch('setLoader', { loading: false });

        return getters.getMainMap;
    },

    customError(store, error) {
        console.log('customError', error);
    },

    async getLayerWfsOutputFormats({ state, getters, dispatch }) {
        state.layers.filter(l => l.data.serviceType === 'WFS' && l.data.feedbackTypeId === 0).forEach(async (layer) => {
            await dispatch('getLayerWfsOutputFormat', { layerData: layer });
        });
    },

    async getOrganizationData({ commit, dispatch, getters }, { webThemeId }) {
        const organizationResult = await dispatch('useApiData', {
            name: 'WebThemeGetOrganization',
            parameters: { id: webThemeId }

        });

        // Only if this call gave us a value
        if (organizationResult.status === 200) {
            const bitmapsResult = await dispatch('useApiData', {
                name: 'WebGetWebThemeBitmaps',
                parameters: { id: webThemeId }
            });

            const organizationResultData = organizationResult.data;
            organizationResultData.locale = organizationResultData.language == 'NL' ? 'nl-NL' : 'en-US';

            const logoData = bitmapsResult.data.find(item => item.imageType === ORGANIZATION_IMAGES.LOGO);
            const favIconData = bitmapsResult.data.find(item => item.imageType === ORGANIZATION_IMAGES.FAVICON);
            if (logoData) {
                commit('SET_LOGO', Buffer.from(logoData.imageData, 'base64').toString());
            }

            // when there is no favicon configured, default favicon is used
            const imageData = favIconData ? Buffer.from(favIconData.imageData, 'base64').toString() : null;
            commit('SET_FAVICON', imageData);

            const themeBaseLayers = await dispatch('getWebThemeBaseLayersLayersById', { webThemeId });
            const baselayers = themeBaseLayers.data;

            // sets the organisation id
            let organizationId = organizationResultData?.id;
            commit('SET_ORGANIZATION_ID', organizationId);

            const organizationBBox = [organizationResultData.minX, organizationResultData.minY, organizationResultData.maxX, organizationResultData.maxY];

            commit('SET_USERDATA', { ...organizationResultData, organizationBBox, organizationId, baselayers });
        }
    },

    async initMainMap({ dispatch, getters, state }) {
        let viewportData;
        // Check if we need to retrieve the webtheme viewport
        // Don't get stored viewport when running in preview of Dashboard webtheme (URL contains parameter previewid)
        const webThemeId = state.webTheme.id;
        if (!window.location.href.includes('previewid') && state.webTheme.data?.saveViewport) {
            // If so get from localstorage with name: VIEWPORT-{ID}
            viewportData = JSON.parse(localStorage.getItem(`${LOCAL_STORAGE_KEYS.VIEWPORT}-${webThemeId}`));
        }

        // use stored viewport data if available
        if (viewportData) {
            dispatch('updateViewPortMap', viewportData);
        } else if (getters.getWebThemeParameters) {
            // otherwise use viewport data as stored in webtheme definition
            dispatch('updateViewPortMap', { zoom: getters.getWebThemeParameters.zoom, center: getters.getWebThemeParameters.center });
        }
        const panelsData = JSON.parse(localStorage.getItem(`${LOCAL_STORAGE_KEYS.UIELEMENTS}-${webThemeId}`));
        if (panelsData) {
            dispatch('updatePanelsData', { panelsData });
        }

        await Promise.all(
            getters.getUserBaseLayers
                .map((layer) => dispatch('addLayer', { type: 'base', layerData: layer, active: true }))
        );

        const firstbaselayer = getters.getBaseLayers[0];
        if (firstbaselayer) {
            await dispatch('addLayerToMap', { mapName: null, id: firstbaselayer.id });
        }

        // add highlightborder for organization if configured
        dispatch('processHightlightBorder');
    },

    // process highlightborder for organization if configured
    async processHightlightBorder({ commit, getters }) {
        const userData = getters.getUserData;
        const webThemeData = getters.getWebTheme.data;
        const webthemeHighlightBorderSetting = (webThemeData.highlightBorder === 'Y');
        const highlightBorder = webThemeData.highlightBorder === 'S' ? userData.highlightBorder : webthemeHighlightBorderSetting;
        if (highlightBorder) {
            const organizationCoordinates = (userData?.coordinates && userData?.coordinates !== '') && userData.coordinates
                .split(' ');
            const coords = getCoordinateArray(organizationCoordinates || squareToPoints(getters.getOrganizationBBox));
            if (coords) {
                commit('MAP_ADD_HIGHLIGHT_BORDER', { mapName: null, coords });
            }
        }
    },

    // need to return:
    // - theme(-layers) (for displaying theme)
    // - filters (attiribute.type = Attribute) (to show selection based on filter)
    // - feedback (attiribute.type = Feedback) (for displaying feedback layer)
    // - FeedbackAttribute (attiribute.type = FeedbackAttribute)
    // - addresses
    // - parcels
    // updates search results after each resolve with suggestions
    async searchFromMenuBar({ commit, dispatch, getters }, { searchText }) {
        if (!searchText) {
            commit('CLEAR_NAVBAR_SEARCH_RESULTS');
        } else {
            // create an object for resolved suggestion searches
            const resolvedSuggestions = {};
            // create promisses for different kind of suggestions
            // const themeSuggestionSearchpromise = dispatch('getSearchSuggestionsForThemes', searchText);
            const addessSuggestionSearchpromise = dispatch('getGeoCode', searchText);
            const getWebthemeAtributeSearchpromise = dispatch('getWebthemeAtributeResult', { searchText, webthemeId: getters.getWebTheme.id });
            // create an array of all suggestion search promisses
            const getSuggestionPromisses = [
                { type: 'webthemes', promise: getWebthemeAtributeSearchpromise },
                { type: 'addresses', promise: addessSuggestionSearchpromise },
            ];
            // show results after eacht promise resolves
            getSuggestionPromisses.forEach(async (suggestionpromise) => {
                // set the result in the object for resolved suggestion searches
                resolvedSuggestions[suggestionpromise.type] = await suggestionpromise.promise;
                // compose all results (defaulting to empty arrays)
                const searchResults = {
                    addresses: resolvedSuggestions.addresses || [],
                    webthemes: resolvedSuggestions.webthemes || [],
                };
                // save the results in the state
                commit('UPDATE_NAVBAR_SEARCH_RESULTS', { searchResults, searchText });
            });
        }
    },

    // 1 close navbar
    // 2 clear current selected features on the map
    // 3 collect data for parcel / address / theme
    // - data for infopanel
    // - features to display in map
    // add selection of features to map
    // focus map on feature(s) if not in view
    async openSearchResultFromMenuBar({ commit, dispatch, state, getters }, { type, data }) {
        const searchLayerForFeaturesPromisses = function (layers, bbox, key, value) {
            return layers.map(({ id }) => dispatch('searchFeaturesOnLayerWithFilter', { bbox, layerId: id, key, value }));
        };
        const map = state.defaultMap.map;

        // 1
        commit('CLEAR_NAVBAR_SEARCH_RESULTS');
        // 2
        commit('RESET_MAP_FEATURES', { mapName: 'main' });
        commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');

        // 3
        if (type === 'address') {
            const result = await dispatch('getLookupData', data.id);
            const address = result;
            // Point
            let point = '';
            if (address.centroide_rd.indexOf('POINT') > -1) {
                point = address.centroide_rd
                    .replace('POINT(', '')
                    .replace(')', '')
                    .split(' ')
                    .map(val => parseFloat(val));
            }

            const resolution = NEDGLOBE_MAP_RESOLUTION[getAddressResolutionIndex(data.type)];
            const zoom = map.getView().getZoomForResolution(resolution);

            // Zoom in to the address selected by the user
            const viewPort = {};
            viewPort.center = point;
            viewPort.zoom = zoom;
            dispatch('updateViewPortMap', viewPort);

            if (address.straatnaam_verkort && point) {
                commit('MAP_ADD_MARKER', { mapName: 'main', point });
            }
            dispatch('setNedGlobePointer', { point: point, address: null });

            commit('UPDATE_SEARCH_SEARCHTEXT', { searchText: address.weergavenaam });
        }

        if (type === 'webtheme') {
            // select layers to search in
            const searchableLayers = state.layers
                .filter((layer) => layer.type === (data.type === 'FeedbackAttribute' ? 'feedback' : 'theme'))
                .map((layer) => {
                    return {
                        ...layer,
                        foundAttribute: layer.data.informationAttributes.some((infoAttr) => infoAttr.attribute === data.key)
                    };
                })
                .filter(({ foundAttribute }) => foundAttribute === true);

            // wait for the responses of the backend, allsettled also resolves when some promisses reject
            let settledFoundFeatures = await Promise
                .allSettled(
                    searchLayerForFeaturesPromisses(
                        // layers
                        searchableLayers,
                        // bbox
                        map.getView().calculateExtent(map.getSize()),
                        // key
                        data.key,
                        // value
                        data.value
                    )
                );
            // collect features from resolved promisses
            let foundFeatures = settledFoundFeatures
                .filter(({ status }) => status === 'fulfilled')
                .flatMap(({ value }) => value);
            // expand search in larger area (organization bbox) when no results are found
            if (foundFeatures.length === 0) {
                settledFoundFeatures = await Promise.allSettled(
                    searchLayerForFeaturesPromisses(
                        // layers
                        searchableLayers,
                        // bbox
                        getters.getOrganizationBBox,
                        // key
                        data.key,
                        // value
                        data.value
                    )
                );
                foundFeatures = settledFoundFeatures
                    .filter(({ status }) => status === 'fulfilled')
                    .flatMap(({ value }) => value);
            }
            // create an array with results to show in the infopanel or in the feedbackform
            const results = searchableLayers.map((layer, index) => {
                dispatch('addLayerToMap', { id: layer.id });
                const olLayer = getters.getMapLayer(null, layer.id).olLayer;
                const theme = getters.getThemeById(layer.data.themeId);
                const meta = {
                    type: layer.type,
                    theme
                };
                return {
                    olLayer,
                    layer,
                    features: settledFoundFeatures[index].status === 'fulfilled' ? settledFoundFeatures[index].value : [],
                    meta,
                    limitedResults: !layerFeaturesCanBePaged(layer) && foundFeatures.length > state.userData.maxFeatures
                };
            });

            if (foundFeatures.length > 0) {
                // determine zoomlevel of map when showing results
                const resolutionIndex = foundFeatures.length === 1 ? 5 : 11;
                const resolutions = NEDGLOBE_MAP_RESOLUTION;
                const resolution = resolutions[resolutions.length - resolutionIndex];
                const maxZoom = map.getView().getZoomForResolution(resolution);
                dispatch('zoomToFeatures', {
                    mapName: 'main',
                    features: foundFeatures,
                    zoomWhenOutOfView: true,
                    options: { padding: [100, 100, 100, 100], duration: DEFAULT_ANIMATION_MS, maxZoom }
                });
            }
            if (data.type === 'FeedbackAttribute') {
                const _data = { location: null, results, type: SELECTION_TYPES.FILTERED };
                dispatch('togglePanel', { panelName: INTERFACE_PANEL_TRIGGERS.WEB_FEEDBACK, visible: true, data: _data });
            } else {
                commit('NEDGLOBE_MOBILE_TOGGLE_FOCUS_INFOPANEL', true);
                commit('ADD_SELECTION', { location: null, results, type: SELECTION_TYPES.FILTERED });
            }
        }
        commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
    },

    hidePanels({ commit, state }, { groupName, panelName, visible }) {
        // only hide panels if a panel is being opened
        const setVisible = visible ?? state.uiElements[panelName].visible === false;
        if (setVisible) {
            // the detailpanel is a child of another panel, we add it seperately [name, value]
            const detailPanel = [INTERFACE_PANEL_TRIGGERS.OBJECT_DETAILS, state.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION].components.detailPanel];
            // create a list of panels based on the ui elements in the root...
            let togglePanelList = [...Object.entries(state.uiElements), detailPanel]
                // ...that have a property position
                .filter(([, value]) => value.position !== undefined)
                // where the position is equal to groupName, if set
                .filter(([, value]) => !groupName || value.position === groupName)
                // ignore the toggled panel
                .filter(([key]) => key !== panelName);

            // do not close legend panel if in webtheme configuration is defined that legend is automatically opened
            if (!state.uiElements.menu.components.legend.close.visible) {
                togglePanelList = togglePanelList.filter(([key]) => key !== 'legend');
            }

            togglePanelList.forEach(([key]) => {
                switch (key) {
                    case INTERFACE_PANEL_TRIGGERS.MENU:
                        commit('TOGGLE_MENU_PANEL', false);
                        break;
                    case INTERFACE_PANEL_TRIGGERS.LEGEND:
                        commit('TOGGLE_LEGEND_PANEL', false);
                        break;
                    case INTERFACE_PANEL_TRIGGERS.OBJECT_DETAILS:
                        commit('TOGGLE_OBJECT_DETAIL_PANEL', false);
                        break;
                    case INTERFACE_PANEL_TRIGGERS.FILTERS:
                        commit('TOGGLE_FILTER_PANEL', false);
                        break;
                    case INTERFACE_PANEL_TRIGGERS.ADVANCED_SEARCH:
                        commit('TOGGLE_ADVANCED_SEARCH_PANEL', false);
                }
            });
        }
    },

    togglePanel({ commit, dispatch }, { panelName, visible, data, mode }) {
        // hide panels, optionally by group (position like 'left')
        const hidePanels = (groupName) => {
            dispatch('hidePanels', { groupName, panelName, visible });
        };

        if (panelName === INTERFACE_PANEL_TRIGGERS.MENU) {
            hidePanels('left');
            commit('TOGGLE_MENU_PANEL', visible);
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.PRINT) {
            hidePanels('left');
            commit('SHOW_MENU_PRINT', true);
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.LEGEND) {
            hidePanels('left');
            commit('TOGGLE_LEGEND_PANEL', visible);
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.FILTERS) {
            hidePanels('left');
            commit('TOGGLE_FILTER_PANEL', visible);
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.ADVANCED_SEARCH) {
            hidePanels('left');
            commit('TOGGLE_ADVANCED_SEARCH_PANEL', visible);
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.OBJECT_DETAILS) {
            hidePanels('left');
            commit('TOGGLE_OBJECT_DETAIL_PANEL', visible);
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.SEARCH_RESULTS) {
            hidePanels();
            commit('TOGGLE_SEARCH_RESULTS', visible);
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.MEASURE_EDIT) {
            hidePanels('left');
            const contentTypeId = CONTENT_TYPE_ID.MEASUREMENT;
            commit('SHOW_MENU_FEEDBACK', { active: visible, contentTypeId, data, mode });
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.CLOSE_FEEDBACK) {
            commit('SHOW_MENU_FEEDBACK', { active: false, contentTypeId: null });
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.WEB_FEEDBACK) {
            commit('TOGGLE_MENU_WEB_FEEDBACK', { active: visible, data });
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.WEB_INFORMATION) {
            commit('TOGGLE_MENU_WEB_INFORMATION', { active: visible, data });
        } else if (panelName === INTERFACE_PANEL_TRIGGERS.WELCOME_PANEL) {
            commit('TOGGLE_WELCOME_PANEL', { active: visible, data });
        }
    },

    // this action needs to be dispatched in the component where subscribe is available
    async updateMainMap({ dispatch, state, commit, getters }, { mutation }) {
        if (
            mutation.type === 'ADD_SELECTION' ||
            mutation.type === 'SET_POINTER'
        ) {
            commit('NEDGLOBE_MOBILE_TOGGLE_FOCUS_INFOPANEL', true);
            commit('MAP_ADD_DRAWING', { mapName: 'main', feature: null });
        }
        if (
            mutation.type === 'CLEAR_SELECTIONS'
        ) {
            commit('RESET_MAP_FEATURES', {});
            dispatch('removeTabObjectSearch', {});
            commit('NEDGLOBE_MOBILE_TOGGLE_FOCUS_INFOPANEL', false);
            commit('MAP_ADD_DRAWING', { mapName: 'main', feature: null });
        }

        if (mutation.payload?.mapName === state.defaultMapName) {
            if (mutation.type === 'MOVE_END') {
                dispatch('setWebfeedbackCanCreate', null);
                dispatch('setInformationTableUpdatebuttondisplay', true);
            }
            if (mutation.type === 'MAP_MOVE_END') {
                const mapName = state.defaultMapName;
                const mapConfig = getters.getMapByName(mapName);
                if (!mapConfig) {
                    throw new Error('map not found', mapName);
                }
                // Get center and zoom from the map
                const { center, zoom } = mapConfig.viewport;
                // Create messages and send it to Dashboard domain
                const previewData = {
                    type: MESSAGE_TYPE.UpdateMaplocation,
                    coordinates: center,
                    zoomLevel: zoom
                };
                const scaleData = {
                    type: MESSAGE_TYPE.GetCurrentScale,
                    scale: Math.round(getScaleFromResolution(
                        mapConfig.map,
                        mapConfig.map.getView().getResolution()
                    ))
                };

                // Update preview map location and scale if webtheme is part of Dashboard
                if (window.location.href.includes('previewid')) {
                    window.top.postMessage(JSON.stringify(previewData), windowMessageReceiver(window.location.origin));
                    window.top.postMessage(JSON.stringify(scaleData), windowMessageReceiver(window.location.origin));
                }

                commit('WEB_MAP_MOVE_END');
            }
            if (!getters.isMarkingSelectionArea) {
                // when a cluster is selected, there should not be another feature selection or pointer placement take place
                if (!clusterSelectionLock) {
                    if (mutation.type === 'MAP_CLICKED') {
                        if (getters.getWebThemeHasFeedbackType && !this.getters.mapIsInSelectableRangeForTerugmeldingen) {
                            commit('SET_CLICKED_IN_RANGE_FOR_WEBFEEDBACK', false);
                        }

                        // Handles point click for a web 'meldingenkaart' that contains line/polygon geometry
                        // This code is called when button to create a new feedback is not clicked yet
                        // Do not create new feedback when clicking in the map, but open an existing feedback if necessary
                        const webFeedbackPanel = state.uiElements[INTERFACE_PANEL_TRIGGERS.WEB_FEEDBACK];
                        if (getters.getWebThemeHasFeedbackType && !getters.getWebThemeFeedbackHasPointGeoOnly && !webFeedbackPanel.showDrawToolbar) {
                            const feedbackLayer = getters.getLayerById(getters.getWebThemeFeedback.layerId);
                            const results = await dispatch('getFeaturesAtPosition', { position: mutation.payload.coordinate, layers: [feedbackLayer] });
                            const data = { location: { position: mutation.payload.coordinate }, results, type: SELECTION_TYPES.LAYERS };

                            if (data.results[0].features.length > 0) {
                                commit('TOGGLE_MENU_WEB_FEEDBACK', {
                                    active: true,
                                    showDrawToolbar: true,
                                    data
                                });
                            }
                        }

                        if (!getters.getWebThemeHasFeedbackType || this.getters.mapIsInSelectableRangeForTerugmeldingen) {
                            // clear pointer
                            commit('SET_POINTER', { location: null });

                            // use the showTableView setting to choose between views
                            // the tableview uses a loader and selections in the store
                            // otherwise invoke the webinformationpanel
                            const useTableView = getters.getWebTheme.data.showTableView;

                            // show loader in resultTable if tableview is used
                            useTableView && commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');
                            // find features at location
                            try {
                                commit('CLEAR_SELECTIONS');
                                if (getters.getWebThemeHasFeedbackType && getters.getWebThemeFeedbackHasPointGeoOnly) {
                                    // add marker
                                    const point = [mutation.payload.coordinate[0], mutation.payload.coordinate[1]];
                                    commit('MAP_ADD_MARKER', { point });

                                    // Handles point click for a web 'meldingenkaart' that has only point geometry
                                    // If web 'meldingenkaart' also has line/polygon geometry, it is handeld in webfeedback\index.vue
                                    const feedbackLayer = getters.getLayerById(getters.getWebThemeFeedback.layerId);
                                    const results = await dispatch('getFeaturesAtPosition', { position: mutation.payload.coordinate, layers: [feedbackLayer] });
                                    const data = { location: { position: mutation.payload.coordinate }, results, type: SELECTION_TYPES.LAYERS };
                                    dispatch('togglePanel', { panelName: INTERFACE_PANEL_TRIGGERS.WEB_FEEDBACK, visible: true, data });
                                } else if (!getters.getWebThemeHasFeedbackType) {
                                    const results = await dispatch('getFeaturesAtPosition', { position: mutation.payload.coordinate });
                                    const data = { location: { position: mutation.payload.coordinate }, results, type: SELECTION_TYPES.LAYERS };

                                    if (!useTableView && !getters.getIsInAccessibleMode) {
                                        dispatch('togglePanel', { panelName: INTERFACE_PANEL_TRIGGERS.WEB_INFORMATION, visible: true, data });
                                    } else {
                                        if (getters.getIsInAccessibleMode) {
                                            const point = [mutation.payload.coordinate[0], mutation.payload.coordinate[1]];
                                            commit('SET_POINTER', { location: { point } });
                                        }
                                        commit('ADD_SELECTION', data);
                                        dispatch('updateSelectionPointer', {});
                                    }
                                }
                            } finally {
                                // hide loader in resultTable if tableview is used
                                useTableView && commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
                            }
                        }
                    }
                }
                if (mutation.type === 'MAP_SELECT_CLUSTER') {
                    clusterSelectionLock = true;
                    const features = mutation.payload.features?.[0].getProperties().features;
                    const mainFeatures = features
                        .filter((_feature) => _feature.getProperties().aansluitnummer === null);
                    const layer = getters.getLayerById(mainFeatures[0].getProperties().layerId);
                    if (getters.mapIsInSelectableRangeForTerugmeldingen || mainFeatures.length === 1) {
                        const results = [{
                            layer,
                            features,
                            meta: { type: layer.type }
                        }];
                        const data = { location: { position: mutation.payload.coordinate }, results, type: SELECTION_TYPES.CLUSTER };
                        if (getters.getWebThemeHasFeedbackType) {
                            dispatch('togglePanel', { panelName: INTERFACE_PANEL_TRIGGERS.WEB_FEEDBACK, visible: true, data });
                        } else {
                            commit('ADD_SELECTION', data);
                        }
                        commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
                    } else {
                        const margin = MAP_MARGINS.clusterSelection;
                        dispatch('zoomToFeatures', {
                            mapName: 'main',
                            features: mainFeatures,
                            zoomWhenOutOfView: false,
                            minResolution: DEFAULT_WEB_CLUSTER_MIN_ZOOM_RESOLUTION,
                            options: { padding: [margin, margin, margin, margin], duration: DEFAULT_ANIMATION_MS },
                        });
                    }
                    setTimeout(
                        () => {
                            clusterSelectionLock = false;
                        }, MAP_INTERACTION_TIMEOUTS.longHold + 100
                    );
                }
            }
        }
    },

    setNedGlobePointer(store, { point }) {
        store.commit('MAP_ADD_MARKER', { point });
        store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
    },

    async createAndDisplaySelectionFromFiltersInWebInfoPanel(store, { filters }) {
        store.dispatch('setLoader', { loading: true });
        const results = await store.dispatch('AddSelectionFromFilters', { filters });
        store.dispatch('togglePanel', { panelName: INTERFACE_PANEL_TRIGGERS.WEB_INFORMATION, visible: true, data: { results } });
        store.dispatch('setLoader', { loading: false });
    },

    displaySelection(store, { data }) {
        // use the showTableView setting to choose between views
        // the tableview uses a loader and selections in the store
        // otherwise invoke the webinformationpanel
        const useTableView = store.getters.getWebTheme?.data?.showTableView || store.getters.getIsInAccessibleMode;

        // show loader in resultTable if tableview is used
        useTableView && store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');

        store.commit('ADD_SELECTION', data);

        try {
            if (!useTableView) {
                store.dispatch('togglePanel', { panelName: INTERFACE_PANEL_TRIGGERS.WEB_INFORMATION, visible: true, data });
            }
        } finally {
            // hide loader in resultTable if tableview is used
            useTableView && store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
        }
    },

    showNedGlobePointer() {
        // Placeholder function.
        // Is being called from tabs.vue. Has a function in mobile. Must be present in web.
    },

    hideNedGlobePointer() {
        // Placeholder function.
        // Is being called from tabs.vue. Has a function in mobile. Must be present in web.
    },
    async getScales({ commit, dispatch }, { webThemeId }) {
        const loadedScales = await dispatch('useApiData',
            {
                name: 'GetMapScalesWeb',
                parameters: { Id: webThemeId }
            });

        commit('SET_SCALES', { scales: JSON.parse(loadedScales.data) });
    },

    // Check incoming TMA ID parameter for web.
    // ID can be either an ID or a GUID.
    // Check which it is and if it is an ID check if webtheme can be used by ID.
    // If it is a GUID get the corresponding ID
    async checkTmaId({ dispatch }, { webThemeParameters }) {
        // check if parameter is an int or a guid or something else (invalid)
        const parameterType = checkTmaValue(webThemeParameters.tma);
        if (parameterType === 'invalid') {
            webThemeParameters.tma = 0;
            return webThemeParameters;
        }

        // Check if this theme can be accessed by id. Return 0 if it isn't.
        if (parameterType === 'integer') {
            const result = await dispatch('useApiData', {
                name: 'getWebThemeIdById',
                parameters: { id: webThemeParameters.tma }
            });
            webThemeParameters.tma = result.data;
            return webThemeParameters;
        } else {
            // If the parameters was a Guid get the Id
            const result = await dispatch('useApiData', {
                name: 'getWebThemeIdByGuid',
                parameters: { guid: webThemeParameters.tma }
            });
            // Place guid in webthemeParameters so it gets saved in the store.
            webThemeParameters.guid = webThemeParameters.tma;
            webThemeParameters.tma = result.data;
            return webThemeParameters;
        }
    },
    updatePanelsData({ state }, { panelsData }) {
        state.uiElements[INTERFACE_PANEL_TRIGGERS.WELCOME_PANEL].dontShowAgain = panelsData.dontShowWelcomePanel;
    },
    startWelcomePanel({ commit, state }, webTheme) {
        if (webTheme?.showWelcomeMessage && !state.uiElements[INTERFACE_PANEL_TRIGGERS.WELCOME_PANEL].dontShowAgain) {
            const data =
            {
                title: webTheme?.welcomeMessageTitle,
                message: webTheme?.welcomeMessage
            };
            commit('TOGGLE_WELCOME_PANEL', { active: true, data });
        }
    }
};
